export type ProgramStatus = "Active" | "Draft" | "Paused" | "Closed";

export type Industry =
  | "travel"
  | "retail"
  | "fnb"
  | "hospitality"
  | "finance"
  | "other";
export type ProgramTimeZone =
  | "Asia/Dubai"
  | "Asia/Kolkata"
  | "Asia/Riyadh"
  | "Europe/London";
export type DataRegion = "UAE" | "India";
export type SupportedLanguage = "en" | "ar" | "hi" | "fr";
export type PointsRounding = "down" | "nearest";
export type PointsExpiryMode = "never" | "months";
export type NegativeBalancePolicy = "allow" | "recover";
export type BonusCombineMode = "multiply" | "add";
export type EnrolmentMode = "open" | "auto" | "invite";
export type MemberIdType = "client" | "hashed";
export type PersonalDataField = "name" | "email" | "phone" | "gender" | "dob";
export type ConsentChannel = "whatsapp" | "sms" | "email";
export type ProgramEventType =
  | "booking.completed"
  | "booking.cancelled"
  | "payment.captured"
  | "member.enrolled"
  | "points.issued"
  | "points.redeemed";

export const CONTACT_ROLES = [
  "Owner",
  "Admin",
  "Marketing",
  "Analyst",
  "Support",
] as const;

export type ContactRole = (typeof CONTACT_ROLES)[number];

export type PerkProgramContact = {
  id: string;
  name: string;
  email: string;
  role: string;
  countryCode: string;
  phoneNumber: string;
};

export type PerkProgramMember = {
  id: string;
  memberId: string;
  name: string;
  email: string;
  initials: string;
  avatarClass: string;
  tier: "Gold" | "Silver" | "Bronze";
  pointsBalance: number;
  status: "Active" | "At risk";
  lastActive: string;
};

export type ProgramConfig = {
  industry: Industry;
  programOwner: string;
  brandPrimaryColor: string;
  billingContactName: string;
  billingContactEmail: string;
  memberTermsUrl: string;
  memberTermsVersion: string;
  privacyPolicyUrl: string;
  privacyPolicyVersion: string;
  timeZone: ProgramTimeZone;
  dataRegion: DataRegion;
  supportedLanguages: SupportedLanguage[];
  pointsRounding: PointsRounding;
  pointsHoldDays: number | null;
  cancellationPeriodDays: number | null;
  pointsExpiryMode: PointsExpiryMode;
  pointsExpiryMonths: number | null;
  negativeBalancePolicy: NegativeBalancePolicy;
  bonusCombineMode: BonusCombineMode;
  economyEffectiveDate: string | null;
  economyNoticeAcknowledged: boolean;
  enrolmentMode: EnrolmentMode;
  memberIdType: MemberIdType;
  personalDataCollected: PersonalDataField[];
  consentChannels: ConsentChannel[];
  dataRetentionMonths: number;
  erasurePolicy: string;
  enabledEventTypes: ProgramEventType[];
  customAttributes: string[];
  webhookUrl: string;
  messagingEnabled: boolean;
  messagingWhatsappNumber: string;
  messagingSmsSenderId: string;
  messagingFromEmail: string;
  quietHoursStart: string;
  quietHoursEnd: string;
  messageFrequencyCap: number;
  offerApprovalThreshold: number;
  adjustmentApprovalThreshold: number;
  supportAgentAdjustmentCap: number;
};

type PerkProgramCore = {
  id: string;
  created: string;
  createdAt: number;
  programLogoUri: string | null;
  programCode: string;
  name: string;
  description: string;
  programType: string;
  status: ProgramStatus;
  address: string;
  currency: string;
  costPerPoint: number;
  invoiceFrequencyInDays: number;
  city: string;
  country: string;
  websiteUrl: string;
  domain: string;
  clientName: string;
  vatNumber: string;
  loyaltyPointsName: string;
  cashbackPercentage: number;
  offerDenomination: number;
  txnCommissionPct: number;
  membersCount: number;
  pointsIssued: string;
  contacts: PerkProgramContact[];
  linkedMemberIds: string[];
  assignedTiers: string[];
  startingTier: string;
};

export type PerkProgram = PerkProgramCore & ProgramConfig;

export const INDUSTRY_OPTIONS: { value: Industry; label: string }[] = [
  { value: "travel", label: "Travel" },
  { value: "retail", label: "Retail" },
  { value: "fnb", label: "F&B" },
  { value: "hospitality", label: "Hospitality" },
  { value: "finance", label: "Finance" },
  { value: "other", label: "Other" },
];

export const TIME_ZONE_OPTIONS: { value: ProgramTimeZone; label: string }[] = [
  { value: "Asia/Dubai", label: "Asia/Dubai" },
  { value: "Asia/Kolkata", label: "Asia/Kolkata" },
  { value: "Asia/Riyadh", label: "Asia/Riyadh" },
  { value: "Europe/London", label: "Europe/London" },
];

export const DATA_REGION_OPTIONS: { value: DataRegion; label: string }[] = [
  { value: "UAE", label: "UAE" },
  { value: "India", label: "India" },
];

export const LANGUAGE_OPTIONS: { value: SupportedLanguage; label: string }[] = [
  { value: "en", label: "English" },
  { value: "ar", label: "Arabic" },
  { value: "hi", label: "Hindi" },
  { value: "fr", label: "French" },
];

export const ROUNDING_OPTIONS: { value: PointsRounding; label: string }[] = [
  { value: "down", label: "Down" },
  { value: "nearest", label: "Nearest" },
];

export const NEGATIVE_BALANCE_OPTIONS: {
  value: NegativeBalancePolicy;
  label: string;
}[] = [
  { value: "allow", label: "Allow" },
  { value: "recover", label: "Take it back from future earnings" },
];

export const BONUS_COMBINE_OPTIONS: { value: BonusCombineMode; label: string }[] =
  [
    { value: "multiply", label: "Multiply" },
    { value: "add", label: "Add" },
  ];

export const ENROLMENT_MODE_OPTIONS: { value: EnrolmentMode; label: string }[] =
  [
    { value: "open", label: "Open" },
    { value: "auto", label: "Auto-enrol on first event" },
    { value: "invite", label: "Invite only" },
  ];

export const MEMBER_ID_TYPE_OPTIONS: { value: MemberIdType; label: string }[] = [
  { value: "client", label: "Client's ID" },
  { value: "hashed", label: "Hashed token" },
];

export const PERSONAL_DATA_OPTIONS: {
  value: PersonalDataField;
  label: string;
}[] = [
  { value: "name", label: "Name" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "gender", label: "Gender" },
  { value: "dob", label: "DOB" },
];

export const CONSENT_CHANNEL_OPTIONS: {
  value: ConsentChannel;
  label: string;
}[] = [
  { value: "whatsapp", label: "WhatsApp" },
  { value: "sms", label: "SMS" },
  { value: "email", label: "Email" },
];

export const EVENT_TYPE_OPTIONS: { value: ProgramEventType; label: string }[] =
  [
    { value: "booking.completed", label: "booking.completed" },
    { value: "booking.cancelled", label: "booking.cancelled" },
    { value: "payment.captured", label: "payment.captured" },
    { value: "member.enrolled", label: "member.enrolled" },
    { value: "points.issued", label: "points.issued" },
    { value: "points.redeemed", label: "points.redeemed" },
  ];

export const CUSTOM_ATTRIBUTE_PRESETS = ["product_type", "payment_mode"] as const;

export const PROGRAM_TIER_CHOICES = [
  {
    name: "Bronze",
    threshold: "0 pts",
    description: "Entry tier for new members",
    swatch: "bg-orange-500",
  },
  {
    name: "Silver",
    threshold: "5,000 pts",
    description: "Regular engagers",
    swatch: "bg-slate-400",
  },
  {
    name: "Gold",
    threshold: "15,000 pts",
    description: "High-value members",
    swatch: "bg-amber-500",
  },
  {
    name: "Platinum",
    threshold: "30,000 pts",
    description: "Top spenders",
    swatch: "bg-sky-600",
  },
  {
    name: "Prestige",
    threshold: "Invite only",
    description: "Exclusive invite-only tier",
    swatch: "bg-violet-600",
  },
] as const;

export function defaultProgramConfig(): ProgramConfig {
  return {
    industry: "retail",
    programOwner: "Program Owner",
    brandPrimaryColor: "#1B7A45",
    billingContactName: "",
    billingContactEmail: "",
    memberTermsUrl: "",
    memberTermsVersion: "",
    privacyPolicyUrl: "",
    privacyPolicyVersion: "",
    timeZone: "Asia/Dubai",
    dataRegion: "UAE",
    supportedLanguages: ["en"],
    pointsRounding: "down",
    pointsHoldDays: null,
    cancellationPeriodDays: null,
    pointsExpiryMode: "months",
    pointsExpiryMonths: 24,
    negativeBalancePolicy: "recover",
    bonusCombineMode: "add",
    economyEffectiveDate: null,
    economyNoticeAcknowledged: false,
    enrolmentMode: "open",
    memberIdType: "client",
    personalDataCollected: ["name", "email", "phone"],
    consentChannels: ["email"],
    dataRetentionMonths: 36,
    erasurePolicy: "Erase personal data within 30 days of a verified request.",
    enabledEventTypes: ["booking.completed", "member.enrolled"],
    customAttributes: [],
    webhookUrl: "",
    messagingEnabled: false,
    messagingWhatsappNumber: "",
    messagingSmsSenderId: "",
    messagingFromEmail: "",
    quietHoursStart: "21:00",
    quietHoursEnd: "08:00",
    messageFrequencyCap: 3,
    offerApprovalThreshold: 5000,
    adjustmentApprovalThreshold: 1000,
    supportAgentAdjustmentCap: 500,
  };
}

export function cloneProgramConfig(config: ProgramConfig): ProgramConfig {
  return {
    ...config,
    supportedLanguages: [...config.supportedLanguages],
    personalDataCollected: [...config.personalDataCollected],
    consentChannels: [...config.consentChannels],
    enabledEventTypes: [...config.enabledEventTypes],
    customAttributes: [...config.customAttributes],
  };
}

/** Anything past Draft: launch-locked fields stay locked (API: "after launch"). */
export function isLaunchedStatus(status: ProgramStatus): boolean {
  return status !== "Draft";
}

export function normalizeAttributeList(
  value: string | readonly string[] | null | undefined,
): string[] {
  const source = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? value.split(",")
      : [];
  const seen = new Set<string>();
  const next: string[] = [];
  source.forEach((item) => {
    const trimmed = item.trim();
    if (!trimmed || seen.has(trimmed)) return;
    seen.add(trimmed);
    next.push(trimmed);
  });
  return next;
}

export function contactsCoverLaunchRoles(
  contacts: readonly { role: string }[],
): boolean {
  const roles = new Set(contacts.map((contact) => contact.role));
  return roles.has("Owner") && roles.has("Admin");
}

/** What a Draft still needs before it can launch. Empty when ready. */
export function programLaunchGaps(
  program: Pick<
    PerkProgram,
    "memberTermsUrl" | "privacyPolicyUrl" | "programOwner" | "contacts"
  >,
): string[] {
  return [
    !program.memberTermsUrl.trim() ? "Member terms URL" : "",
    !program.privacyPolicyUrl.trim() ? "Privacy policy URL" : "",
    !program.programOwner.trim() ? "Program owner" : "",
    program.contacts.some((contact) => contact.role === "Owner")
      ? ""
      : "Owner contact",
    program.contacts.some((contact) => contact.role === "Admin")
      ? ""
      : "Admin contact",
  ].filter(Boolean);
}

export function isFutureIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value.trim())) return false;
  const selected = new Date(`${value.trim()}T00:00:00`);
  if (Number.isNaN(selected.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return selected.getTime() > today.getTime();
}

export function samePointEconomy(
  current: {
    costPerPoint: number;
    pointsRounding: PointsRounding;
    pointsExpiryMode: PointsExpiryMode;
    pointsExpiryMonths: number | null;
    bonusCombineMode: BonusCombineMode;
  },
  next: {
    costPerPoint: number;
    pointsRounding: PointsRounding;
    pointsExpiryMode: PointsExpiryMode;
    pointsExpiryMonths: number | null;
    bonusCombineMode: BonusCombineMode;
  },
): boolean {
  const money = (value: number) => Math.round(value * 10000) / 10000;
  return (
    money(current.costPerPoint) === money(next.costPerPoint) &&
    current.pointsRounding === next.pointsRounding &&
    current.pointsExpiryMode === next.pointsExpiryMode &&
    current.pointsExpiryMonths === next.pointsExpiryMonths &&
    current.bonusCombineMode === next.bonusCombineMode
  );
}

export function formatVersionedUrl(url: string, version: string): string {
  const link = url.trim();
  const note = version.trim();
  if (link && note) return `${link} · ${note}`;
  return link || note;
}

function withProgramConfig(
  program: PerkProgramCore,
  overrides: Partial<ProgramConfig> = {},
): PerkProgram {
  const config = cloneProgramConfig({
    ...defaultProgramConfig(),
    ...overrides,
    customAttributes: normalizeAttributeList(
      overrides.customAttributes ?? defaultProgramConfig().customAttributes,
    ),
  });
  return {
    ...config,
    ...program,
  };
}

export function contactRoleOptions(currentRole: string): string[] {
  const roles: string[] = [...CONTACT_ROLES];
  if (currentRole && !roles.includes(currentRole)) roles.push(currentRole);
  return roles;
}

export function optionLabel(
  options: readonly { value: string; label: string }[],
  value: string,
): string {
  return options.find((option) => option.value === value)?.label ?? value;
}

export function labelsFor(
  options: readonly { value: string; label: string }[],
  selected: readonly string[],
): string {
  return selected
    .map((value) => optionLabel(options, value))
    .filter(Boolean)
    .join(", ");
}

export function formatHoldDays(days: number | null): string {
  if (days == null) return "None";
  return `${days} day${days === 1 ? "" : "s"}`;
}

export function formatCancellationPeriod(days: number | null): string {
  if (days == null) return "None";
  return `${days} day${days === 1 ? "" : "s"}`;
}

export function formatPointsExpiry(
  mode: PointsExpiryMode,
  months: number | null,
): string {
  if (mode === "never") return "Never";
  if (months == null) return "";
  return `${months} month${months === 1 ? "" : "s"}`;
}

export function formatPerDay(count: number): string {
  return `${count} per day`;
}

export function preserveAddOnly<T extends string>(
  locked: boolean,
  previous: readonly T[],
  next: readonly T[],
): T[] {
  if (!locked) return [...next];
  const merged = [...previous];
  next.forEach((item) => {
    if (!merged.includes(item)) merged.push(item);
  });
  return merged;
}

export const MOCK_MEMBER_POOL: PerkProgramMember[] = [
  {
    id: "m1",
    memberId: "MEM-001",
    name: "Dummy Member",
    email: "dummy.member1@email.com",
    initials: "DM",
    avatarClass: "bg-emerald-100 text-emerald-800",
    tier: "Gold",
    pointsBalance: 12480,
    status: "Active",
    lastActive: "Today",
  },
  {
    id: "m2",
    memberId: "MEM-002",
    name: "Dummy Member",
    email: "dummy.member2@email.com",
    initials: "DM",
    avatarClass: "bg-sky-100 text-sky-800",
    tier: "Silver",
    pointsBalance: 4820,
    status: "Active",
    lastActive: "Yesterday",
  },
  {
    id: "m3",
    memberId: "MEM-003",
    name: "Dummy Member",
    email: "dummy.member3@email.com",
    initials: "DM",
    avatarClass: "bg-amber-100 text-amber-800",
    tier: "Gold",
    pointsBalance: 22100,
    status: "Active",
    lastActive: "2 days ago",
  },
  {
    id: "m4",
    memberId: "MEM-004",
    name: "Dummy Member",
    email: "dummy.member4@email.com",
    initials: "DM",
    avatarClass: "bg-violet-100 text-violet-800",
    tier: "Bronze",
    pointsBalance: 980,
    status: "At risk",
    lastActive: "12 days ago",
  },
  {
    id: "m5",
    memberId: "MEM-005",
    name: "Dummy Member",
    email: "dummy.member5@email.com",
    initials: "DM",
    avatarClass: "bg-rose-100 text-rose-800",
    tier: "Silver",
    pointsBalance: 6540,
    status: "Active",
    lastActive: "Today",
  },
  {
    id: "m6",
    memberId: "MEM-006",
    name: "Dummy Member",
    email: "dummy.member6@email.com",
    initials: "DM",
    avatarClass: "bg-teal-100 text-teal-800",
    tier: "Gold",
    pointsBalance: 15800,
    status: "Active",
    lastActive: "3 days ago",
  },
  {
    id: "m7",
    memberId: "MEM-007",
    name: "Dummy Member",
    email: "dummy.member7@email.com",
    initials: "DM",
    avatarClass: "bg-indigo-100 text-indigo-800",
    tier: "Bronze",
    pointsBalance: 420,
    status: "At risk",
    lastActive: "21 days ago",
  },
  {
    id: "m8",
    memberId: "MEM-008",
    name: "Dummy Member",
    email: "dummy.member8@email.com",
    initials: "DM",
    avatarClass: "bg-lime-100 text-lime-800",
    tier: "Silver",
    pointsBalance: 9100,
    status: "Active",
    lastActive: "Yesterday",
  },
];

const indiaBaseline: Partial<ProgramConfig> = {
  timeZone: "Asia/Kolkata",
  dataRegion: "India",
  pointsHoldDays: 1,
  customAttributes: ["product_type", "payment_mode"],
  billingContactName: "Accounts Payable",
  billingContactEmail: "billing@perkpoint.dummy",
  memberTermsVersion: "1",
  privacyPolicyVersion: "1",
};

const SEED_OVERRIDES: Record<string, Partial<ProgramConfig>> = {
  "1": {
    industry: "retail",
    programOwner: "Dummy Contact",
    brandPrimaryColor: "#1B7A45",
    memberTermsUrl: "https://perkretaill.dummy/terms",
    privacyPolicyUrl: "https://perkretaill.dummy/privacy",
    supportedLanguages: ["en"],
    consentChannels: ["sms", "email"],
    enabledEventTypes: [
      "booking.completed",
      "payment.captured",
      "member.enrolled",
    ],
    cancellationPeriodDays: 7,
  },
  "2": {
    industry: "fnb",
    programOwner: "Café Manager",
    brandPrimaryColor: "#C2410C",
    memberTermsUrl: "https://perkcafe.dummy/terms",
    privacyPolicyUrl: "https://perkcafe.dummy/privacy",
    enrolmentMode: "auto",
    enabledEventTypes: ["payment.captured", "member.enrolled"],
    customAttributes: ["product_type"],
    messagingEnabled: true,
    messagingFromEmail: "hello@perkcafe.dummy",
    messagingSmsSenderId: "PERKCAFE",
    messagingWhatsappNumber: "+971500000002",
  },
  "3": {
    industry: "travel",
    programOwner: "Travel Admin",
    brandPrimaryColor: "#0369A1",
    memberTermsUrl: "https://perktravel.dummy/terms",
    privacyPolicyUrl: "https://perktravel.dummy/privacy",
    supportedLanguages: ["en", "ar"],
    pointsExpiryMode: "never",
    pointsExpiryMonths: null,
    enrolmentMode: "invite",
    memberIdType: "hashed",
    consentChannels: ["email", "whatsapp"],
    enabledEventTypes: [
      "booking.completed",
      "booking.cancelled",
      "member.enrolled",
    ],
    cancellationPeriodDays: 14,
    webhookUrl: "https://perktravel.dummy/hooks/loyalty",
  },
  "4": {
    industry: "retail",
    programOwner: "Fashion Lead",
    brandPrimaryColor: "#7C3AED",
    memberTermsUrl: "https://perkfashion.dummy/terms",
    privacyPolicyUrl: "https://perkfashion.dummy/privacy",
    pointsRounding: "nearest",
    bonusCombineMode: "multiply",
    personalDataCollected: ["name", "email", "phone", "dob"],
  },
  "5": {
    industry: "other",
    programOwner: "Lifestyle Owner",
    brandPrimaryColor: "#B45309",
    memberTermsUrl: "",
    memberTermsVersion: "",
    privacyPolicyUrl: "",
    privacyPolicyVersion: "",
    enrolmentMode: "invite",
    pointsExpiryMonths: 36,
    pointsHoldDays: null,
  },
  "6": {
    industry: "retail",
    programOwner: "Store Ops",
    brandPrimaryColor: "#15803D",
    memberTermsUrl: "https://perkgrocery.dummy/terms",
    privacyPolicyUrl: "https://perkgrocery.dummy/privacy",
    pointsRounding: "nearest",
    enrolmentMode: "auto",
    pointsHoldDays: null,
    messageFrequencyCap: 3,
  },
};

const PROGRAM_SEEDS: PerkProgramCore[] = [
  {
    id: "1",
    created: "07 Oct 2025, 10:24 AM",
    createdAt: new Date("2025-10-07T10:24:00").getTime(),
    programLogoUri: null,
    programCode: "PERKRETAIL",
    name: "Perk Retail Rewards",
    description: "Earn and redeem across Dummy Retail partner outlets.",
    programType: "EARN",
    status: "Active",
    address: "12 MG Road, Bengaluru",
    currency: "INR",
    costPerPoint: 0.25,
    invoiceFrequencyInDays: 30,
    city: "Bengaluru",
    country: "India",
    websiteUrl: "https://perkretaill.dummy",
    domain: "perkretaill.dummy",
    clientName: "Dummy Retail Client",
    vatNumber: "VAT-IN-1001",
    loyaltyPointsName: "Retail Points",
    cashbackPercentage: 5,
    offerDenomination: 1,
    txnCommissionPct: 2,
    membersCount: 3,
    pointsIssued: "1.2M",
    contacts: [
      {
        id: "c1",
        name: "Dummy Contact",
        email: "ops@perkretaill.dummy",
        role: "Program Admin",
        countryCode: "+91",
        phoneNumber: "9876543210",
      },
    ],
    linkedMemberIds: ["m1", "m2", "m3"],
    assignedTiers: ["Bronze", "Silver", "Gold", "Platinum"],
    startingTier: "Bronze",
  },
  {
    id: "2",
    created: "18 Sep 2025, 02:15 PM",
    createdAt: new Date("2025-09-18T14:15:00").getTime(),
    programLogoUri: null,
    programCode: "PERKCAFE",
    name: "Perk Café Club",
    description: "Coffee loyalty for Dummy Café locations.",
    programType: "EARN",
    status: "Active",
    address: "88 Bandra West, Mumbai",
    currency: "INR",
    costPerPoint: 0.2,
    invoiceFrequencyInDays: 15,
    city: "Mumbai",
    country: "India",
    websiteUrl: "https://perkcafe.dummy",
    domain: "perkcafe.dummy",
    clientName: "Dummy Café Client",
    vatNumber: "VAT-IN-1002",
    loyaltyPointsName: "Brew Points",
    cashbackPercentage: 8,
    offerDenomination: 1,
    txnCommissionPct: 1.5,
    membersCount: 2,
    pointsIssued: "420K",
    contacts: [
      {
        id: "c2",
        name: "Café Manager",
        email: "manager@perkcafe.dummy",
        role: "Operations",
        countryCode: "+91",
        phoneNumber: "9123456780",
      },
    ],
    linkedMemberIds: ["m4", "m5"],
    assignedTiers: ["Bronze", "Silver", "Gold", "Platinum"],
    startingTier: "Bronze",
  },
  {
    id: "3",
    created: "02 Aug 2025, 09:40 AM",
    createdAt: new Date("2025-08-02T09:40:00").getTime(),
    programLogoUri: null,
    programCode: "PERKTRAVEL",
    name: "Perk Travel Miles",
    description: "Travel earn and burn for Dummy Travel bookings.",
    programType: "EARN",
    status: "Paused",
    address: "Terminal 3, New Delhi",
    currency: "INR",
    costPerPoint: 0.5,
    invoiceFrequencyInDays: 30,
    city: "New Delhi",
    country: "India",
    websiteUrl: "https://perktravel.dummy",
    domain: "perktravel.dummy",
    clientName: "Dummy Travel Client",
    vatNumber: "VAT-IN-1003",
    loyaltyPointsName: "Travel Miles",
    cashbackPercentage: 3,
    offerDenomination: 10,
    txnCommissionPct: 2.5,
    membersCount: 1,
    pointsIssued: "890K",
    contacts: [],
    linkedMemberIds: ["m6"],
    assignedTiers: ["Bronze", "Silver", "Gold", "Platinum"],
    startingTier: "Bronze",
  },
  {
    id: "4",
    created: "22 Jul 2025, 04:05 PM",
    createdAt: new Date("2025-07-22T16:05:00").getTime(),
    programLogoUri: null,
    programCode: "PERKFASHION",
    name: "Perk Fashion Points",
    description: "Style rewards across Dummy Fashion brands.",
    programType: "EARN",
    status: "Active",
    address: "Phoenix Marketcity, Pune",
    currency: "INR",
    costPerPoint: 0.3,
    invoiceFrequencyInDays: 45,
    city: "Pune",
    country: "India",
    websiteUrl: "https://perkfashion.dummy",
    domain: "perkfashion.dummy",
    clientName: "Dummy Fashion Client",
    vatNumber: "VAT-IN-1004",
    loyaltyPointsName: "Style Points",
    cashbackPercentage: 6,
    offerDenomination: 1,
    txnCommissionPct: 2,
    membersCount: 2,
    pointsIssued: "310K",
    contacts: [
      {
        id: "c4",
        name: "Fashion Lead",
        email: "lead@perkfashion.dummy",
        role: "Marketing",
        countryCode: "+91",
        phoneNumber: "9988776655",
      },
    ],
    linkedMemberIds: ["m7", "m8"],
    assignedTiers: ["Bronze", "Silver", "Gold", "Platinum"],
    startingTier: "Bronze",
  },
  {
    id: "5",
    created: "11 Jun 2025, 11:50 AM",
    createdAt: new Date("2025-06-11T11:50:00").getTime(),
    programLogoUri: null,
    programCode: "PERKGOLD",
    name: "Perk Gold Lifestyle",
    description: "Premium lifestyle program for Dummy Prestige members.",
    programType: "EARN",
    status: "Draft",
    address: "Jubilee Hills, Hyderabad",
    currency: "INR",
    costPerPoint: 0.4,
    invoiceFrequencyInDays: 30,
    city: "Hyderabad",
    country: "India",
    websiteUrl: "https://perkgold.dummy",
    domain: "perkgold.dummy",
    clientName: "Dummy Lifestyle Client",
    vatNumber: "VAT-IN-1005",
    loyaltyPointsName: "Gold Points",
    cashbackPercentage: 10,
    offerDenomination: 5,
    txnCommissionPct: 3,
    membersCount: 0,
    pointsIssued: "0",
    contacts: [],
    linkedMemberIds: [],
    assignedTiers: ["Bronze", "Silver", "Gold", "Platinum"],
    startingTier: "Bronze",
  },
  {
    id: "6",
    created: "03 May 2025, 08:20 AM",
    createdAt: new Date("2025-05-03T08:20:00").getTime(),
    programLogoUri: null,
    programCode: "PERKGROCERY",
    name: "Perk Grocery Plus",
    description: "Everyday grocery earn for Dummy Mart shoppers.",
    programType: "EARN",
    status: "Active",
    address: "Anna Nagar, Chennai",
    currency: "INR",
    costPerPoint: 0.15,
    invoiceFrequencyInDays: 7,
    city: "Chennai",
    country: "India",
    websiteUrl: "https://perkgrocery.dummy",
    domain: "perkgrocery.dummy",
    clientName: "Dummy Grocery Client",
    vatNumber: "VAT-IN-1006",
    loyaltyPointsName: "Basket Points",
    cashbackPercentage: 4,
    offerDenomination: 1,
    txnCommissionPct: 1,
    membersCount: 1,
    pointsIssued: "156K",
    contacts: [
      {
        id: "c6",
        name: "Store Ops",
        email: "ops@perkgrocery.dummy",
        role: "Support",
        countryCode: "+91",
        phoneNumber: "9000011122",
      },
    ],
    linkedMemberIds: ["m1"],
    assignedTiers: ["Bronze", "Silver", "Gold", "Platinum"],
    startingTier: "Bronze",
  },
];

const INITIAL_PROGRAMS: PerkProgram[] = PROGRAM_SEEDS.map((program) =>
  withProgramConfig(program, {
    ...indiaBaseline,
    ...SEED_OVERRIDES[program.id],
  }),
);

function copyProgram(program: PerkProgram): PerkProgram {
  return {
    ...program,
    ...cloneProgramConfig(program),
    contacts: program.contacts.map((contact) => ({ ...contact })),
    linkedMemberIds: [...program.linkedMemberIds],
    assignedTiers: [...(program.assignedTiers ?? [])],
  };
}

let programsStore: PerkProgram[] = INITIAL_PROGRAMS.map(copyProgram);

export function getPrograms(): PerkProgram[] {
  return programsStore.map(copyProgram);
}

export function getProgramById(id: string): PerkProgram | undefined {
  const found = programsStore.find((program) => program.id === id);
  if (!found) return undefined;
  return copyProgram(found);
}

export function upsertProgram(program: PerkProgram): PerkProgram {
  const index = programsStore.findIndex((item) => item.id === program.id);
  const next = copyProgram({
    ...program,
    membersCount: program.linkedMemberIds.length,
  });
  if (index >= 0) {
    programsStore[index] = next;
  } else {
    programsStore = [next, ...programsStore];
  }
  return getProgramById(next.id)!;
}

export function deleteProgram(id: string): void {
  programsStore = programsStore.filter((program) => program.id !== id);
}

export function linkMembersToProgram(
  programId: string,
  memberIds: string[],
): PerkProgram | undefined {
  const program = programsStore.find((item) => item.id === programId);
  if (!program) return undefined;
  const merged = Array.from(new Set([...program.linkedMemberIds, ...memberIds]));
  program.linkedMemberIds = merged;
  program.membersCount = merged.length;
  return getProgramById(programId);
}

export function unlinkMemberFromProgram(
  programId: string,
  memberId: string,
): PerkProgram | undefined {
  const program = programsStore.find((item) => item.id === programId);
  if (!program) return undefined;
  program.linkedMemberIds = program.linkedMemberIds.filter(
    (linkedId) => linkedId !== memberId,
  );
  program.membersCount = program.linkedMemberIds.length;
  return getProgramById(programId);
}

export function addContactToProgram(
  programId: string,
  contact: Omit<PerkProgramContact, "id">,
): PerkProgram | undefined {
  const program = programsStore.find((item) => item.id === programId);
  if (!program) return undefined;
  program.contacts = [
    ...program.contacts,
    { ...contact, id: `c-${Date.now()}` },
  ];
  return getProgramById(programId);
}

export function removeContactFromProgram(
  programId: string,
  contactId: string,
): PerkProgram | undefined {
  const program = programsStore.find((item) => item.id === programId);
  if (!program) return undefined;
  program.contacts = program.contacts.filter(
    (contact) => contact.id !== contactId,
  );
  return getProgramById(programId);
}

/** @deprecated use getPrograms */
export const MOCK_PERK_PROGRAMS = INITIAL_PROGRAMS;
